import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { PartyPopper, Hand, Bluetooth, ArrowRight, ChevronLeft } from "lucide-react";
import TopNav from "@/components/TopNav";
import { TOTAL_STEPS } from "@/data/lampContent";
import { readSavedVolunteer, stepCountOf } from "@/data/volunteerFlows";

const CHARACTER_SRC =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_solar-lamp-builder/artifacts/karnivl7_two-people-giving-high-five-illustration-2025-10-20-04-29-40-utc.gif";

export default function Done() {
  const navigate = useNavigate();
  const stepsDone = useMemo(() => {
    const v = readSavedVolunteer();
    if (!v) return TOTAL_STEPS;
    // Include the parts-checklist "Step 1" alongside the volunteer's own items.
    return stepCountOf(v) + 1;
  }, []);

  useEffect(() => {
    // Fire a celebratory confetti burst on mount.
    const end = Date.now() + 800;
    const colors = ["#d64533", "#F5B72C", "#ffffff"];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="done-page"
    >
      <TopNav />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 py-10 flex items-center">
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="card-brut bg-[var(--primary)] overflow-hidden w-full"
          data-testid="done-card"
        >
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 p-6 sm:p-10 items-center">
            <div className="sm:col-span-2 flex justify-center overflow-hidden">
              <img
                src={CHARACTER_SRC}
                alt="Your friendly build guide celebrating"
                className="w-full max-w-sm h-auto scale-150 origin-center"
                data-testid="done-character"
              />
            </div>

            <div className="sm:col-span-3 text-left">
              <div className="text-center sm:text-left">
                <div className="arcade-btn arcade-btn-ghost text-[10px] px-3 py-1.5">
                  <PartyPopper
                    strokeWidth={3}
                    className="h-4 w-4 text-[var(--accent)]"
                  />
                  <span
                    data-testid="done-badge"
                  >
                    All {stepsDone} steps done
                  </span>
                </div>
              </div>

              <h1
                className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-tight text-center sm:text-left"
                data-testid="done-title"
              >
                You{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">did it!</span>
                  <span
                    aria-hidden
                    className="absolute -bottom-1 left-0 right-0 h-3 bg-white/70 -rotate-2 rounded-sm z-0"
                  />
                </span>
              </h1>

              <p
                className="mt-4 text-lg text-[var(--text-main)] leading-relaxed"
                data-testid="done-body"
              >
                Your Game Designer Kit is ready! Flip the switch on and test
                it out with the Dabble app — try forward, backward, left and
                right to make sure everything moves.
              </p>

              <p
                className="mt-4 font-hand text-2xl text-[var(--text-main)] -rotate-2 inline-flex items-center gap-2"
                data-testid="done-signoff"
              >
                High-five, builder.
                <Hand strokeWidth={2.5} className="h-6 w-6" />
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/final-packing"
                  data-testid="done-connect-btn"
                  className="arcade-btn"
                >
                  <Bluetooth strokeWidth={3} className="h-4 w-4" />
                  Final Packing
                  <ArrowRight strokeWidth={3} className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </motion.article>
      </main>

      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
        data-testid="done-bottom-nav"
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="done-prev-btn"
          className="arcade-btn arcade-btn-icon"
        >
          <ChevronLeft strokeWidth={3} className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
